/**
 * BadgeGrid — Badge display with earned/unearned states (Step 24)
 * Updated Phase 5 P0: Added BadgeDetailModal on click
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Chip,
  Avatar,
  CardActionArea,
} from '@mui/material';
import {
  EmojiEvents,
  Lock,
  CheckCircle,
} from '@mui/icons-material';
import BadgeDetailModal from './BadgeDetailModal';

// Map badge_key → emoji fallback when no icon_url
const BADGE_ICONS = {
  first_interview: '🎤',
  five_interviews: '🏆',
  perfect_score: '💯',
  seven_day_streak: '🔥',
  thirty_day_streak: '🌟',
  resume_uploaded: '📄',
  speed_demon: '⚡',
  community_connector: '🤝',
};

const BadgeCard = ({ badge, onClick }) => {
  const {
    badge_key,
    name,
    description,
    icon_url,
    earned,
    earned_at,
  } = badge;

  const emoji = BADGE_ICONS[badge_key] || '🏅';
  const earnedDate = earned_at
    ? new Date(earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Tooltip
      title={
        earned
          ? `${description} — Earned ${earnedDate}`
          : `${description} — Not yet earned`
      }
      arrow
    >
      <Card
        elevation={earned ? 2 : 0}
        sx={{
          height: '100%',
          opacity: earned ? 1 : 0.5,
          border: earned ? '2px solid' : '1px dashed',
          borderColor: earned ? 'primary.main' : 'divider',
          transition: 'all 0.2s',
          cursor: 'pointer',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: earned ? 4 : 1 },
        }}
        data-testid={`badge-card-${badge_key}`}
      >
        <CardActionArea onClick={() => onClick && onClick(badge)} sx={{ height: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 2, px: 1 }}>
          {icon_url ? (
            <Avatar src={icon_url} alt={name} sx={{ width: 48, height: 48, mx: 'auto', mb: 1 }} />
          ) : (
            <Typography variant="h4" sx={{ mb: 1 }}>
              {emoji}
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}>
            {name}
          </Typography>
          {earned ? (
            <Chip
              icon={<CheckCircle />}
              label="Earned"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          ) : (
            <Chip
              icon={<Lock />}
              label="Locked"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
};

const BadgeGrid = ({ badges = [], totalEarned = 0, totalAvailable = 0 }) => {
  const [selectedBadge, setSelectedBadge] = useState(null);

  return (
    <Box data-testid="badge-grid">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEvents color="warning" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Badges
          </Typography>
        </Box>
        <Chip
          label={`${totalEarned} / ${totalAvailable}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>
      <Grid container spacing={1.5}>
        {badges.map((badge) => (
          <Grid item xs={6} sm={4} md={3} key={badge.badge_key}>
            <BadgeCard badge={badge} onClick={setSelectedBadge} />
          </Grid>
        ))}
      </Grid>

      {/* Badge detail modal */}
      <BadgeDetailModal
        open={!!selectedBadge}
        onClose={() => setSelectedBadge(null)}
        badge={selectedBadge}
      />
    </Box>
  );
};

export default BadgeGrid;
